// Fix the formatMessage function to properly handle text styling and remove duplication

import React, { useState, useEffect,useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Divider, Chip, Button,
  List, ListItem, ListItemText, IconButton, Tooltip, Paper, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Alert, Snackbar, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Tab, Tabs, useMediaQuery
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  VolumeUp as VolumeUpIcon,
  Translate as TranslateIcon,
  Add as AddIcon,
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon,
  MenuBook as MenuBookIcon,
  School as SchoolIcon,
  Chat as ChatIcon,
  Bookmark as BookmarkIcon,
  Warning as WarningIcon,
  SentimentSatisfied as SmileyIcon,
  SentimentDissatisfied as SadIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import AudioPlayer from '../components/audioPlayer';
import useAudioPlayer from '../hooks/useAudioPlayer';
import WordDialog from '../components/WordDialog';
import MessageBubble from '../components/MessageBubble';
import theme from "../theme";

// Knowledge level options for vocabulary
const knowledgeLevels = [
  { value: 1, label: "Don't Know", color: 'error', icon: <SadIcon /> },
  { value: 2, label: 'Learning', color: 'warning', icon: <HelpIcon /> },
  { value: 3, label: 'Know Well', color: 'success', icon: <SmileyIcon /> }
];



const SessionReviewPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [learningSummary, setLearningSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [audioJobs, setAudioJobs] = useState({});
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioReadyMap, setAudioReadyMap] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [sessionVocabulary, setSessionVocabulary] = useState([]);
  const [loadingVocabulary, setLoadingVocabulary] = useState(false);
  const { audioPlayerState, openAudioPlayer, closeAudioPlayer } = useAudioPlayer();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Feedback dialog state (for viewing suggestions)
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [activeFeedback, setActiveFeedback] = useState(null);

  // Audio URL cache
  const [audioUrls, setAudioUrls] = useState(new Map());

  // Get or create audio URL and cache it
  const getAudioUrl = (messageContent) => {
    const jobId = audioJobs[messageContent];
    if (!jobId) return null;

    // Check if we already have this URL cached
    const cacheKey = `${messageContent}-${jobId}`;
    if (audioUrls.has(cacheKey)) {
      return audioUrls.get(cacheKey);
    }

    // Create new URL and cache it
    const url = apiClient.audio.getAudioUrl(jobId);
    setAudioUrls(prev => new Map(prev.set(cacheKey, url)));
    return url;
  };


  // Function to preload voices
  const preloadVoices = () => {
    if (window.speechSynthesis) {
      // Get voices - this may return an empty array initially
      const voices = window.speechSynthesis.getVoices();

      if (voices.length === 0) {
        // If voices aren't loaded yet, set up an event listener
        window.speechSynthesis.onvoiceschanged = () => {
          console.log("Speech synthesis voices loaded:", window.speechSynthesis.getVoices().length);
        };

        // Force initialization to trigger the event
        window.speechSynthesis.getVoices();
      } else {
        console.log("Speech synthesis voices already loaded:", voices.length);
      }
    } else {
      console.warn("Speech synthesis not supported in this browser");
    }
  };

  // Function to play word pronunciation
  const playWordPronunciation = (word, language) => {
    if (!word) return;

    try {
      // Check if speech synthesis is available
      if (!window.speechSynthesis) {
        console.error("Browser does not support speech synthesis");
        showNotification("Your browser doesn't support text-to-speech", "warning");
        return;
      }

      // Stop any current speech
      window.speechSynthesis.cancel();

      // Create a new utterance
      const utterance = new SpeechSynthesisUtterance(word);

      // Set language based on the session language
      // Convert full language names to BCP 47 language tags
      let langCode = language || session?.target_language;
      if (langCode) {
        // Map common language names to BCP 47 codes
        const languageMap = {
          'english': 'en-US',
          'french': 'fr-FR',
          'spanish': 'es-ES',
          'german': 'de-DE',
          'italian': 'it-IT',
          'japanese': 'ja-JP',
          'portuguese': 'pt-PT',
          'romanian': 'ro-RO'
        };

        langCode = languageMap[langCode.toLowerCase()] || langCode;
        utterance.lang = langCode;
      }

      // Set voice properties
      utterance.rate = 0.8; // Slightly slower for language learning
      utterance.pitch = 1.0;

      // Optionally, try to select an appropriate voice
      setTimeout(() => {
        const voices = window.speechSynthesis.getVoices();
        const matchingVoice = voices.find(voice =>
          voice.lang.startsWith(langCode.split('-')[0]) &&
          !voice.name.includes('Google') // Skip Google voices as they may sound robotic
        );

        if (matchingVoice) {
          utterance.voice = matchingVoice;
        }

        // Play the pronunciation
        window.speechSynthesis.speak(utterance);
      }, 100); // Small delay to ensure voices are loaded

    } catch (error) {
      console.error("Error using speech synthesis:", error);
      showNotification("Error playing pronunciation", "error");
    }
  };


  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Word dialog state
  const [wordDialogOpen, setWordDialogOpen] = useState(false);
  const [selectedWord, setSelectedWord] = useState(null);

  // Audio reference
  const audioRef = useRef(new Audio());


  const unescapeHTML = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.documentElement.textContent;
  };

  // Fetch session data
  // Enhanced transcript deduplication logic for useEffect
useEffect(() => {
  const fetchSessionData = async () => {
    setLoading(true);
    preloadVoices();
    try {
      // Get session transcript
      const response = await apiClient.sessions.getSessionTranscript(sessionId);
      //console.log(response);
      setSession({
        session_id: response.data.session_id,
        content_title: response.data.content_title,
        content_type: response.data.content_type,
        target_language: response.data.target_language,
        proficiency_level: response.data.proficiency_level,
        glossary: response.data.glossary || {}
      });

      fetchSessionVocabulary(response.data.target_language);

      // Process translations to decode HTML entities
      if (response.data.transcript && Array.isArray(response.data.transcript)) {
        // Process all translations to decode HTML entities
        response.data.transcript.forEach(entry => {
          if (entry.translation) {
            entry.translation = unescapeHTML(entry.translation);
          }

          if (!entry.message_id) {
            entry.message_id = `msg_${entry.order}_${Math.random().toString(36).substring(2, 10)}`;
          }

          // Check if this entry already has audio_file reference
          if (entry.audio_file) {
            setAudioJobs(prev => ({ ...prev, [entry.message]: entry.audio_file }));
            setAudioReadyMap(prev => ({ ...prev, [entry.message]: true }));
          }


          const innerEntry = entry;
          if (typeof entry.message === 'string' && entry.message.includes('"response"')) {
            try {
              // Parse the JSON string into an object
              const messageObject = JSON.parse(entry.message);

              // Access the response property
              const responseText = messageObject.response;
              console.log('Response::::::::', responseText);

              // Replace only the message text with the response text
              entry.message = responseText;

              // Note: We're NOT replacing the string with the parsed object anymore
              // This was: innerEntry.message = messageObject;
            } catch (error) {
              console.error('Error parsing JSON:', error);
              console.log('Original message:', entry.message);
            }
          } else {
            // Message is already a plain string, no parsing needed
          }


        });
      }


      // Completely new approach to handle transcript data
      if (response.data.transcript && Array.isArray(response.data.transcript)) {
        // Create a map to detect duplicates using full order + role combination
        // This prevents any duplicates from the server side
        const uniqueMessages = {};

        response.data.transcript.forEach(entry => {
          const key = `${entry.message}-${entry.role}`;
          if (entry.message && entry.message.includes('[SYSTEM_ADJUSTMENT')) {
            //console.log('Skipping system adjustment message');
            return; // Skip this iteration
          }
          // Only keep the first occurrence of each unique order-role combination
          if (!uniqueMessages[key]) {
            uniqueMessages[key] = entry;
          } else {
            //console.log(`Duplicate message detected and filtered: ${key}`);
          }
        });

        // Convert back to array and sort
        const dedupedTranscript = Object.values(uniqueMessages)
          .sort((a, b) => a.order - b.order);

        // Process transcript to move corrections/suggestions from agent messages
        // to their preceding user messages (matching how LearningSessionPage handles it)
        // Also transform 'message' field to 'content' for MessageBubble compatibility
        const processedTranscript = dedupedTranscript.map((entry, index, array) => {
          // Transform message field to content for MessageBubble
          const processed = {
            ...entry,
            content: entry.message, // MessageBubble expects 'content'
            role: entry.role === 'agent' ? 'assistant' : entry.role // Normalize role name
          };

          return processed;
        });

        // Second pass: move corrections from agent messages to preceding user messages
        for (let i = 0; i < processedTranscript.length; i++) {
          const entry = processedTranscript[i];

          // If this is an agent/assistant message with corrections or suggestions
          if (entry.role === 'assistant' && (entry.corrections?.length > 0 || entry.suggestions)) {
            // Find the preceding user message
            for (let j = i - 1; j >= 0; j--) {
              if (processedTranscript[j].role === 'user') {
                // Move corrections and suggestions to the user message
                processedTranscript[j].corrections = entry.corrections;
                processedTranscript[j].suggestions = entry.suggestions;
                break;
              }
            }
          }
        }

        console.log(`Processed transcript with ${processedTranscript.length} entries`);
        setTranscript(processedTranscript);
      } else {
        console.warn("Missing or invalid transcript data");
        setTranscript([]);
      }

      // Check if we have a learning summary
      if (response.data.learning_summary && Object.keys(response.data.learning_summary).length > 0) {
        setLearningSummary(response.data.learning_summary);
      }

      // Fetch audio links if available
      fetchAudioLinks();
    } catch (error) {
      console.error('Error fetching session data:', error);
      showNotification('Failed to load session data', 'error');
    } finally {
      setLoading(false);
    }
  };

  fetchSessionData();
}, [sessionId]);

  // Clean up audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Fetch audio links
  const fetchAudioLinks = async () => {
    try {
      const response = await apiClient.sessions.getSessionAudioLinks(sessionId);

      // Map the audio links to the transcript messages
      const audioLinks = response.data.audio_links || {};

      // Update audioReadyMap based on available audio links
      const readyMap = {};
      const jobsMap = {};

      for (const [text, link] of Object.entries(audioLinks)) {
        readyMap[text] = true;

        // Extract job ID from link path (assuming format /api/audio/JOB_ID)
        const jobId = link.split('/').pop();
        jobsMap[text] = jobId;
      }

      setAudioReadyMap(readyMap);
      setAudioJobs(jobsMap);

    } catch (error) {
      console.error('Error fetching audio links:', error);
    }
  };

  const getTranslationForWord = (word) => {
    if (!session?.glossary || !word) return '';
    //console.log(session.glossary);
    //console.log(session.word);
    // Try exact match first
    if (session.glossary[word]) {
      return session.glossary[word];
    }

    // Try case-insensitive match
    const lowerCaseWord = word.toLowerCase();
    const keys = Object.keys(session.glossary);

    for (const key of keys) {
      if (key.toLowerCase() === lowerCaseWord) {
        return session.glossary[key];
      }
    }

    return '';
  };

  const formatMessage = (text, isAssistant = false) => {
  if (!text) return '';

  // For user messages, just return plain text (no vocabulary styling)
  // This preserves the parent container's text color (white on blue background)
  if (!isAssistant) {
    return text.split('\n').map((line, i, arr) => (
      <React.Fragment key={`line-${i}`}>
        {line}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  }

  // For assistant messages, apply vocabulary word styling
  // Split the text into words, preserving spaces and punctuation
  const words = text.split(/(\s+|[.,;:!?"()[\]{}])/);

  return words.map((word, index) => {
    // Skip empty strings and whitespace
    if (!word.trim()) {
      return word;
    }

    // Skip punctuation
    if (/^[.,;:!?"()[\]{}]$/.test(word)) {
      return word;
    }

    // Clean the word (remove punctuation) for vocabulary lookup
    const cleanWord = word.replace(/^[.,;:!?"()[\]{}]+|[.,;:!?"()[\]{}]+$/g, '').trim();

    if (!cleanWord) return word;

    // Check if the word is in our vocabulary (case-insensitive)
    const vocabWord = sessionVocabulary.find(v =>
      v.word.toLowerCase() === cleanWord.toLowerCase()
    );

    // Get translation regardless for tooltip
    const translation = getTranslationForWord(cleanWord);
    const tooltipText = translation || vocabWord?.translation || "Click to add to vocabulary";

    if (vocabWord) {
      // Return styled word based on knowledge level
      const knowledgeColor = getKnowledgeLevelColor(vocabWord.knowledge_level);
      return (
        <Tooltip key={index} title={tooltipText}>
          <Box
            component="span"
            onClick={() => handleOpenWordDialog(vocabWord)}
            sx={{
              cursor: 'pointer',
              color: 'text.primary',
              borderBottom: '1px dotted rgba(10, 49, 97, 0.3)', // Light dotted underline with primary color
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                color: 'primary.main',
                borderBottom: '1px dotted #0A3161' // Darker dotted underline on hover
              }
            }}
          >
            {word}
          </Box>
        </Tooltip>
      );
    }

    // For words not in vocabulary, make them clickable to add
    return (
      <Tooltip key={index} title={tooltipText}>
        <Box
          component="span"
          onClick={() => handleOpenWordDialog({
            word: cleanWord,
            translation: translation || '',
            context: text,  // Use containing text as context
            language_code: session?.target_language,
            knowledge_level: 1
          })}
          sx={{
            cursor: 'pointer',
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              color: 'primary.main'
            }
          }}
        >
          {word}
        </Box>
      </Tooltip>
    );
  });
};

// Add the getKnowledgeLevelColor helper function to SessionReviewPage
const getKnowledgeLevelColor = (level) => {
  switch (level) {
    case 1:
      return 'error';
    case 2:
      return 'warning';
    case 3:
      return 'success';
    default:
      return 'primary';
  }
};

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);

    // If switching to Summary tab and we don't have a summary yet, generate one
    if (newValue === 1 && !learningSummary && !summaryLoading) {
      generateLearningSummary();
    }
  };

  // Generate learning summary
  const generateLearningSummary = async () => {
    setSummaryLoading(true);
    try {
      const response = await apiClient.sessions.getLearningSummary(sessionId);
      setLearningSummary(response.data.learning_summary);
    } catch (error) {
      console.error('Error generating learning summary:', error);
      showNotification('Failed to generate learning summary', 'error');
    } finally {
      setSummaryLoading(false);
    }
  };

  // Play audio
  const playAudio = async (text, messageId = null) => {
    try {
      // Get or request job ID for this text
      let jobId = audioJobs[text];
      if (!jobId) {
        jobId = await requestAudio(text, session?.target_language, null, messageId);
        if (!jobId) return;
      }

      // Get audio URL
      const audioUrl = apiClient.audio.getAudioUrl(jobId);

      // Use our audio player
      openAudioPlayer(audioUrl, `${session?.content_title || 'Session'} - Audio`, messageId);
      setAudioPlaying(true);
    } catch (error) {
      console.error("Error playing audio:", error);
      setAudioPlaying(false);
    }
  };

  const generateAllAudio = async () => {
    try {
      setGeneratingAudio(true);

      // Request audio generation for all transcript entries
      const response = await apiClient.sessions.generateTranscriptAudio(sessionId);

      // For each request, add the message_id to the audio generation request
      if (response.data.audio_requests && response.data.audio_requests.length > 0) {
        for (const request of response.data.audio_requests) {
          // Find matching transcript entry for this order
          const entry = transcript.find(e => e.order === request.order);
          if (entry && entry.message) {
            // Request specific audio with message ID
            await requestAudio(entry.message, session?.target_language, entry.message_id);
          }
        }
      }

      showNotification(`Generating audio for ${response.data.audio_requests?.length || 0} messages`, 'info');

      // Start polling for audio status updates
      setTimeout(() => {
        fetchAudioLinks();
        setGeneratingAudio(false);
      }, 5000); // Wait 5 seconds before checking for updates

    } catch (error) {
      console.error('Error generating transcript audio:', error);
      showNotification('Failed to generate audio', 'error');
      setGeneratingAudio(false);
    }
  };

  // Request audio generation
  const requestAudio = async (text, language, messageId = null) => {
    try {
      const response = await apiClient.audio.generateAudio({
        text,
        language: language || session?.target_language,
        voice_type: 'STANDARD',
        speaking_rate: 1.0,
        session_id: session?.session_id,
        message_id: messageId // Pass the message ID to associate with the audio
      });

      if (response.data && response.data.job_id) {
        console.log("Audio generation requested, job ID:", response.data.job_id);

        // Check if the response indicates that existing audio was reused
        if (response.data.reused) {
          console.log("Reused existing audio with job ID:", response.data.job_id);
          setAudioReadyMap(prev => ({ ...prev, [text]: true }));
        } else {
          console.log("generating new audi with job ID: ",response.data.job_id);
          // Start polling for audio status for newly generated audio
          pollAudioStatus(response.data.job_id, text);
        }

        // Store job ID with the text
        setAudioJobs(prev => ({
          ...prev,
          [text]: response.data.job_id
        }));

        return response.data.job_id;
      }
    } catch (error) {
      console.error("Error requesting audio:", error);
    }

    return null;
  };


  // Poll for audio status
  const pollAudioStatus = async (jobId, text) => {
    try {
      const checkStatus = async () => {
        const response = await apiClient.audio.getAudioStatus(jobId);

        if (response.data.status === 'completed') {
          setAudioReadyMap(prev => ({ ...prev, [text]: true }));
          console.log("Audio generation completed for job:", jobId);
          return true;
        } else if (response.data.status === 'error') {
          console.error("Audio generation failed:", response.data.message);
          return true;
        }

        return false;
      };

      // Start polling
      const poll = async () => {
        const isComplete = await checkStatus();
        if (!isComplete) {
          setTimeout(poll, 2000); // Check every 2 seconds
        }
      };

      poll();
    } catch (error) {
      console.error("Error polling audio status:", error);
    }
  };

  // Open word dialog for adding to vocabulary
  const handleOpenWordDialog = (wordData) => {
  // Handle different types of input
  if (typeof wordData === 'string') {
    // If it's a string, create a word object
    const cleanWord = wordData.replace(/^[.,;:!?'"()[\]{}]+|[.,;:!?'"()[\]{}]+$/g, '').trim();

    // Get translation from glossary
    const translation = getTranslationForWord(cleanWord);

    setSelectedWord({
      word: cleanWord,
      translation: translation || '',
      context: '',
      language_code: session?.target_language,
      knowledge_level: 1,
      tags:[]
    });
  } else if (typeof wordData === 'object' && wordData !== null) {
    // If it's already a word object (from vocabulary), use it directly
    if (wordData.id) {
      setSelectedWord(wordData);
    } else {
      // It's a new word created from clicking text
      const cleanWord = wordData.word.replace(/^[.,;:!?'"()[\]{}]+|[.,;:!?'"()[\]{}]+$/g, '').trim();
      const translation = getTranslationForWord(cleanWord);

      setSelectedWord({
        ...wordData,
        word: cleanWord,
        translation: translation || wordData.translation || ''
      });
    }
  } else {
    console.error("Invalid word data:", wordData);
    return;
  }

  setWordDialogOpen(true);
};

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      // Close the audio player
      closeAudioPlayer();
    };
  }, [closeAudioPlayer]);

  // Close word dialog
  const handleCloseWordDialog = () => {
    setWordDialogOpen(false);
  };

  // Save word to vocabulary
  const handleSaveWord = async (wordData) => {
    try {
      if (wordData.id) {
        // Update existing word
        await apiClient.vocabulary.updateVocabularyItem(
          wordData.id,
          {
            translation: wordData.translation,
            context: wordData.context,
            knowledge_level: wordData.knowledge_level,
            tags: wordData.tags // Added tags support
          }
        );

        // Update local state if you have vocabulary state
        fetchSessionVocabulary(session.target_language);

        showNotification('Word updated successfully', 'success');
      } else {
        // Add new word
        const response = await apiClient.vocabulary.addVocabularyItem({
          word: wordData.word,
          translation: wordData.translation,
          context: wordData.context,
          knowledge_level: wordData.knowledge_level,
          language_code: wordData.language_code || session?.target_language,
          tags: wordData.tags // Added tags support
        });

        // Update local state
        fetchSessionVocabulary(session.target_language);

        showNotification('Word added to vocabulary', 'success');
      }

      handleCloseWordDialog();
    } catch (error) {
      console.error('Error saving vocabulary word:', error);

      if (error.response?.data?.error?.includes('already in your vocabulary')) {
        showNotification('This word is already in your vocabulary', 'info');
      } else {
        showNotification('Failed to save vocabulary word', 'error');
      }
    }
  };

  // Add multiple words to vocabulary
  const handleAddWordsToVocabulary = async (words) => {
    if (!words || words.length === 0) return;

    try {
      const response = await apiClient.sessions.addWordsToVocabulary(sessionId, {
        words: words
      });

      showNotification(`Added ${response.data.added_words.length} words to vocabulary`, 'success');
    } catch (error) {
      console.error('Error adding words to vocabulary:', error);
      showNotification('Failed to add words to vocabulary', 'error');
    }
  };

  const fetchSessionVocabulary = async (languageCode) => {
    if (!languageCode) return;
    console.log('languageCode: ' + languageCode)
    setLoadingVocabulary(true);
    try {
      // Convert full language names to language codes if needed
      let code = languageCode.toLowerCase();
      if (code.length > 2) {
        // Map common language names to codes
        const languageMap = {
          'french': 'fr',
          'spanish': 'es',
          'german': 'de',
          'italian': 'it',
          'japanese': 'ja',
          'romanian': 'ro',
          'portuguese': 'pt',
          'english': 'en'
        };
        code = languageMap[code] || code.slice(0, 2);
      }

      const response = await apiClient.vocabulary.getVocabulary({ language_code: code });
      setSessionVocabulary(response.data.vocabulary || []);
    } catch (error) {
      console.error('Error fetching vocabulary:', error);
    } finally {
      setLoadingVocabulary(false);
    }
  };

  // Show notification
  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  // Navigate back to sessions list
  const handleBackToSessions = () => {
    navigate('/sessions');
  };

  // Render transcript messages using MessageBubble component
  // This matches how LearningSessionPage renders messages
  const renderTranscript = () => {
    // First check if we actually have transcript data to prevent empty rendering
    if (!transcript || transcript.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1">No transcript data available</Typography>
        </Box>
      );
    }

    return (
      <Box>
        {transcript.map((message, index) => {
          // Skip any malformed messages
          if (!message || !message.role) {
            console.warn("Skipping malformed message at index", index);
            return null;
          }

          return (
            <MessageBubble
              key={`message-${index}-${message.order}-${message.role}`}
              message={message}
              isUser={message.role === 'user'}
              isSystem={message.role === 'system'}
              onOpenWordDialog={handleOpenWordDialog}
              onFeedback={(m) => { setActiveFeedback(m); setFeedbackDialogOpen(true); }}
              getAudioUrl={getAudioUrl}
              audioReady={audioReadyMap[message.content]}
              formatMessage={formatMessage}
            />
          );
        })}
      </Box>
    );
  };


  // Render the learning summary
  const renderLearningSummary = () => {
    if (summaryLoading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1">Generating Learning Summary...</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This may take a minute as we analyze your conversation
          </Typography>
        </Box>
      );
    }

    if (!learningSummary) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <Typography variant="body1" gutterBottom>No learning summary available yet</Typography>
          <Button
            variant="contained"
            onClick={generateLearningSummary}
            startIcon={<SchoolIcon />}
          >
            Generate Learning Summary
          </Button>
        </Box>
      );
    }

    return (
      <Box>
        {/* Key Vocabulary */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Key Vocabulary</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {learningSummary.key_vocabulary?.map((item, index) => (
                <ListItem key={index} divider={index < learningSummary.key_vocabulary.length - 1}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ mr: 1 }}>{item.word}</Typography>
                        <Chip size="small" label={item.translation} variant="outlined" />
                        <IconButton
                          size="small"
                          onClick={() => handleOpenWordDialog(item.word, item.translation, item.context)}
                          sx={{ ml: 'auto' }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                    secondary={item.context}
                  />
                </ListItem>
              ))}
            </List>

            <Button
              variant="outlined"
              color="primary"
              sx={{ mt: 2 }}
              onClick={() => handleAddWordsToVocabulary(learningSummary.key_vocabulary)}
              startIcon={<BookmarkIcon />}
            >
              Add All to Vocabulary
            </Button>
          </AccordionDetails>
        </Accordion>

        {/* Grammar Points */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Grammar Concepts</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {learningSummary.grammar_points?.map((item, index) => (
                <ListItem key={index} divider={index < learningSummary.grammar_points.length - 1}>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1">{item.concept}</Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body2">{item.explanation}</Typography>
                        {item.example && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="body2" fontStyle="italic">"{item.example}"</Typography>
                          </Box>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Corrections */}
        {learningSummary.corrections && learningSummary.corrections.length > 0 && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Corrections</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {learningSummary.corrections.map((item, index) => (
                  <ListItem key={index} divider={index < learningSummary.corrections.length - 1}>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="body1" color="error" sx={{ textDecoration: 'line-through' }}>
                            {item.original}
                          </Typography>
                          <Typography variant="body1" color="success.main" sx={{ mt: 0.5 }}>
                            {item.correction}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {item.explanation}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Weak points tracked across sessions */}
        {(learningSummary.recurring_feedback || learningSummary.weak_points?.length > 0) && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Across Your Sessions</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {learningSummary.recurring_feedback && (
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  {learningSummary.recurring_feedback}
                </Typography>
              )}
              {learningSummary.weak_points?.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {learningSummary.weak_points.map((label) => (
                    <Chip key={label} label={label} size="small" variant="outlined" color="warning" />
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                Weak points from this conversation are tracked on your Progress page, and the next summary checks whether they come up again.
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Improvement Suggestions */}
        {learningSummary.suggestions && learningSummary.suggestions.length > 0 && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Suggestions for Improvement</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {learningSummary.suggestions.map((suggestion, index) => (
                  <ListItem key={index} divider={index < learningSummary.suggestions.length - 1}>
                    <ListItemText
                      primary={suggestion}
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    );
  };

  // Main render
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box  sx={{pb: '60px'}}>
      {/* Session header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={handleBackToSessions} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h5">
                  {session?.content_title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Chip
                    label={session?.target_language}
                    size="small"
                    color="primary"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={session?.proficiency_level}
                    size="small"
                    color="secondary"
                  />
                  <Chip
                    label={session?.content_type === 'story' ? 'Story' : 'Conversation'}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 1 }}
                    icon={session?.content_type === 'story' ? <MenuBookIcon /> : <ChatIcon />}
                  />

                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{alignItems: 'center', alignContent:'center'}}>
          <Button
                    variant="outlined"
                    startIcon={<VolumeUpIcon />}
                    onClick={generateAllAudio}
                    disabled={generatingAudio}
                    sx={{ ml: 2, mt : 2, alignContent: 'center' }}
                  >
                    {generatingAudio ?
                      <CircularProgress size={24} /> :
                      'Generate All Audio'
                    }
                  </Button>
        </Box>
        </CardContent>
      </Card>


      {/* Add the AudioPlayer component */}
      <AudioPlayer
        audioSrc={audioPlayerState.audioSrc}
        title={audioPlayerState.title}
        onClose={() => {
          closeAudioPlayer();
          setAudioPlaying(false);
        }}
        autoPlay={true}
        position="bottom"
      />

      {/* Content tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} centered>
          <Tab label="Transcript" icon={<ChatIcon />} iconPosition="start" />
          <Tab label="Learning Summary" icon={<SchoolIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab content */}
      <Box sx={{ mb: 3, pb : '38px' }}>
      {/* Only render the active tab content */}
      {selectedTab === 0 && (
        <Box key="transcript-tab">
          {renderTranscript()}
        </Box>
      )}

      {selectedTab === 1 && (
        <Box key="summary-tab">
          {renderLearningSummary()}
        </Box>
      )}
    </Box>

      {/* Add to Vocabulary Dialog */}
      <WordDialog
        open={wordDialogOpen}
        onClose={handleCloseWordDialog}
        word={selectedWord}
        onSave={handleSaveWord}
        disableAutoFocus={isMobile}
        languageCode={session?.target_language}
        playPronunciation={playWordPronunciation}
      />

      {/* Feedback Dialog for viewing suggestions */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Learning Feedback</DialogTitle>
        <DialogContent>
          {activeFeedback?.suggestions && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Suggestions:
              </Typography>
              <Typography variant="body2">
                {typeof activeFeedback.suggestions === 'string'
                  ? activeFeedback.suggestions
                  : Array.isArray(activeFeedback.suggestions)
                    ? activeFeedback.suggestions.join('. ')
                    : ''}
              </Typography>
            </Box>
          )}
          {activeFeedback?.corrections && activeFeedback.corrections.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Corrections:
              </Typography>
              {activeFeedback.corrections.map((correction, idx) => (
                <Box key={idx} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="error" sx={{ textDecoration: 'line-through' }}>
                    {correction.original}
                  </Typography>
                  <Typography variant="body2" color="success.main" fontWeight="medium">
                    {correction.correction}
                  </Typography>
                  {correction.explanation && (
                    <Typography variant="caption" color="text.secondary">
                      {correction.explanation}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SessionReviewPage;